import { useMemo } from 'react';

import { EventResponse, PlanningEntityType } from 'generated';
import { useTranslation } from 'react-i18next';
import { getViewContext } from '../../utils';
import { ViewContext } from '../../enums';

function useGetColumnsByEntityType(
  entityType: PlanningEntityType,
  events: EventResponse[]
) {
  const { t } = useTranslation();
  const isMyActivityPlanning = getViewContext() === ViewContext.Own;
  const multiplePlanUnits =
    events
      .map((event) => event.organizationalUnit?.id)
      .filter((value, index, self) => self.indexOf(value) === index).length > 1;
  const columns = useMemo(() => {
    switch (entityType) {
      case 'CLIENT':
        return [
          {
            field: 'timeWindow',
            headerName: t('activityPlanning.timeWindow'),
            width: 148
          },
          {
            field: 'duration',
            headerName: t('duration'),
            width: 60
          },
          {
            field: 'activity',
            headerName: t('activity')
          },
          {
            field: 'client',
            headerName: t('client')
          },
          {
            field: 'employee',
            headerName: t('employee')
          },
          ...(multiplePlanUnits
            ? [
                {
                  field: 'organizationalUnit',
                  headerName: t('organizationalUnit')
                }
              ]
            : []),
          ...(events.some((event) => event.appointmentGrouping)
            ? [
                {
                  field: 'appointmentGrouping',
                  headerName: t('activityPlanning.route')
                }
              ]
            : []),
          {
            field: 'recurrenceRule.recurrenceRuleRepresentation',
            headerName: t('recurrenceRule')
          },
          {
            field: 'signal',
            headerName: '',
            width: 80
          }
        ];
      case 'EMPLOYEE':
        return [
          ...(events.some((event) => !event.project)
            ? [
                {
                  field: 'timeWindow',
                  headerName: t('activityPlanning.timeWindow'),
                  width: 148
                }
              ]
            : []),
          {
            field: 'duration',
            headerName: t('duration'),
            width: 60
          },
          ...(events.some((event) => event.project)
            ? [
                {
                  field: 'project',
                  headerName: t('activityPlanning.project')
                }
              ]
            : []),

          ...(events.some((event) => event.workPlace)
            ? [
                {
                  field: 'workPlace',
                  headerName: t('activityPlanning.workPlace')
                }
              ]
            : []),
          {
            field: 'activity',
            headerName: t('activity')
          },
          ...(events.some((event) => event.clients?.[0])
            ? [
                {
                  field: 'client',
                  headerName: t('client')
                }
              ]
            : []),
          {
            field: 'employee',
            headerName: t('employee')
          },
          ...(multiplePlanUnits
            ? [
                {
                  field: 'organizationalUnit',
                  headerName: t('organizationalUnit')
                }
              ]
            : []),
          ...(events.some((event) => event.appointmentGrouping)
            ? [
                {
                  field: 'appointmentGrouping',
                  headerName: t('activityPlanning.route')
                }
              ]
            : []),
          ...(events.some((event) => event.recurrenceRule)
            ? [
                {
                  field: 'recurrenceRule.recurrenceRuleRepresentation',
                  headerName: t('recurrenceRule')
                }
              ]
            : []),
          ...(events.some((event) => event.status)
            ? [
                {
                  field: 'status',
                  headerName: t('status'),
                  width: 100
                }
              ]
            : []),
          {
            field: 'noteInternal',
            headerName: t('remark')
          },
          {
            field: 'signal',
            headerName: '',
            width: 80
          }
        ];
      case 'WORKPLACE':
        return [
          {
            field: 'duration',
            headerName: t('duration'),
            width: 108
          },
          {
            field: 'workplace',
            headerName: t('activityPlanning.planningEntityType.WORKPLACE')
          },
          {
            field: 'workPlaceDescription',
            headerName: t('description')
          },
          {
            field: 'activity',
            headerName: t('activity')
          },
          {
            field: 'employee',
            headerName: t('employee')
          },
          ...(multiplePlanUnits
            ? [
                {
                  field: 'organizationalUnit',
                  headerName: t('organizationalUnit')
                }
              ]
            : []),
          {
            field: 'noteInternal',
            headerName: t('remark')
          },
          {
            field: 'signal',
            headerName: '',
            width: 80
          }
        ];
      case 'PROJECT':
        return [
          {
            field: 'duration',
            headerName: t('duration'),
            width: 108
          },
          {
            field: 'project',
            headerName: t('activityPlanning.planningEntityType.PROJECT')
          },
          {
            field: 'activity',
            headerName: t('activity')
          },
          ...(!isMyActivityPlanning
            ? [
                {
                  field: 'employee',
                  headerName: t('employee')
                }
              ]
            : []),
          ...(multiplePlanUnits
            ? [
                {
                  field: 'organizationalUnit',
                  headerName: t('organizationalUnit')
                }
              ]
            : []),
          {
            field: 'status',
            headerName: t('status'),
            width: 100
          },
          {
            field: 'noteInternal',
            headerName: t('remark')
          },
          {
            field: 'signal',
            headerName: '',
            width: 80
          }
        ];
      default:
        return [];
    }
  }, [events, entityType]);

  return columns;
}

export default useGetColumnsByEntityType;
